import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { Button } from '@progress/kendo-react-buttons';
import { DropDownList, DropDownListChangeEvent } from '@progress/kendo-react-dropdowns';
import { NumericTextBox, TextArea } from '@progress/kendo-react-inputs';
import { Loader } from '@progress/kendo-react-indicators';
import { toDataSourceRequestString } from '@progress/kendo-data-query';
import { fetchApi } from '../../../services/api';
import { JsonResponse } from '../../../TypeGen/json-response';
import { FieldWrapper } from '@progress/kendo-react-form';
import { Error, Label } from '@progress/kendo-react-labels';

interface LocationViewModel {
  LocationID: number;
  Name: string;
}

interface LocationsResponse {
  Items: {
    Data: LocationViewModel[];
    Total: number;
  };
}

interface RestockPartDialogProps {
  partId: number;
  partNumber: string;
  unitOfMeasure: string;
  defaultLocationId?: number | null;
  onClose: () => void;
  onSuccess: () => void;
}

const RestockPartDialog: React.FC<RestockPartDialogProps> = ({
  partId,
  partNumber,
  unitOfMeasure,
  defaultLocationId,
  onClose,
  onSuccess
}) => {
  const [locations, setLocations] = useState<LocationViewModel[]>([]);
  const [locationId, setLocationId] = useState<number | null>(null);
  const [quantity, setQuantity] = useState<number | null>(0);
  const [costPerUnit, setCostPerUnit] = useState<number | null>(0);
  const [purchaseOrderNumber, setPurchaseOrderNumber] = useState<number | null>(null);
  const [note, setNote] = useState<string>('');
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadLocations = async () => {
      setLoadingLocations(true);
      try {
        const request = toDataSourceRequestString({
          skip: 0,
          take: 1000,
          sort: [{ field: 'Name', dir: 'asc' }]
        });
        const response: LocationsResponse = await fetchApi(`/api/ShopInventory/Locations?${request}`);
        setLocations(response.Items.Data);
        if (response.Items.Data.length > 0) {
          const lastUsed = response.Items.Data.find(x => x.LocationID === defaultLocationId);
          setLocationId(lastUsed?.LocationID ?? response.Items.Data[0].LocationID);
        }
      } catch (err) {
        console.error('Failed to load locations', err);
        setError('Failed to load locations.');
      } finally {
        setLoadingLocations(false);
      }
    };

    loadLocations();
  }, [defaultLocationId]);

  const locationValue = useMemo(
    () => locations.find(x => x.LocationID === locationId) ?? null,
    [locations, locationId]
  );

  const noLocationsAvailable = !loadingLocations && locations.length === 0;

  const resetAndClose = () => {
    setLocationId(null);
    setQuantity(0);
    setCostPerUnit(0);
    setPurchaseOrderNumber(null);
    setNote('');
    setError(null);
    onClose();
  };

  const handleSave = async () => {
    if (!locationId) {
      setError('Please select a location.');
      return;
    }

    if (!quantity || quantity <= 0) {
      setError('Quantity must be greater than zero.');
      return;
    }

    if (costPerUnit === null || costPerUnit < 0) {
      setError('Cost per unit cannot be negative.');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const response: JsonResponse = await fetchApi('/api/ShopInventory/RestockPart', {
        PartId: partId,
        LocationId: locationId,
        Quantity: quantity,
        CostPerUnit: costPerUnit,
        Note: note?.trim() ? note.trim() : null,
        PurchaseOrderNumber: purchaseOrderNumber ?? null
      }, 'POST');

      if (response.Success === false) {
        setSubmitting(false);
        setError(response.ErrorMessage || 'Failed to restock part.');
        return;
      }

      onSuccess();
      resetAndClose();
    } catch (err: any) {
      if (!err?.status) {
        alert('Failed to restock part.');
      }
      setSubmitting(false);
      setError(err?.detail ?? 'Failed to restock part.');
    }
  };

  return (
    <Dialog
      title={`Restock ${partNumber}`}
      onClose={resetAndClose}
      className="dialog-w500"
    >
      <div className='k-form k-form-md mb-3'>
        {error && <Error>{error}</Error>}

        <FieldWrapper>
          <Label>Location</Label>
          {loadingLocations ? (
            <div className="d-flex align-items-center">
              <Loader type="converging-spinner" size="medium" />
              <span className="ms-2">Loading locations...</span>
            </div>
          ) : (
            <DropDownList
              data={locations}
              textField="Name"
              dataItemKey="LocationID"
              value={locationValue}
              onChange={(e: DropDownListChangeEvent) => {
                const item = e.value as LocationViewModel | null;
                setLocationId(item?.LocationID ?? null);
              }}
              disabled={submitting}
              style={{ width: '100%' }}
            />
          )}
          {noLocationsAvailable && (
            <div className="alert alert-warning mt-2" role="alert">
              No inventory locations are available. Create a location first to restock this part.
            </div>
          )}
        </FieldWrapper>

        <div className="row">
          <div className="col-md-6">
            <FieldWrapper>
              <Label>Quantity</Label>
              <NumericTextBox
                value={quantity}
                onChange={(e) => setQuantity(e.value ?? null)}
                min={1}
                step={1}
                format="n0"
                disabled={submitting}
                style={{ width: '100%' }}
              />
            </FieldWrapper>
          </div>
          <div className="col-md-6">
            <FieldWrapper>
              <Label>Cost Per {unitOfMeasure}</Label>
              <NumericTextBox
                value={costPerUnit}
                onChange={(e) => setCostPerUnit(e.value ?? null)}
                min={0}
                step={0.01}
                format="c2"
                disabled={submitting}
                style={{ width: '100%' }}
              />
            </FieldWrapper>
          </div>
          <div className="col-md-6">
            <FieldWrapper>
              <Label>Purchase Order #</Label>
              <NumericTextBox
                value={purchaseOrderNumber}
                onChange={(e) => setPurchaseOrderNumber(e.value ?? null)}
                min={1}
                format="0"
                spinners={false}
                placeholder="Enter PO # (Optional)"
                disabled={submitting}
                style={{ width: '100%' }}
              />
            </FieldWrapper>
          </div>
        </div>

        <FieldWrapper>
          <Label>Notes</Label>
          <TextArea
            rows={3}
            value={note}
            onChange={(e) => setNote(e.value)}
            disabled={submitting}
          />
        </FieldWrapper>
      </div>
      <DialogActionsBar>
        <Button
          type="button"
          onClick={resetAndClose}
          disabled={submitting}
        >
          Cancel
        </Button>
        <Button
          type="button"
          themeColor="primary"
          disabled={submitting || loadingLocations || noLocationsAvailable}
          onClick={handleSave}
        >
          {submitting ? 'Restocking...' : 'Restock'}
        </Button>
      </DialogActionsBar>
    </Dialog>
  );
};

export default RestockPartDialog;
